import React, { ButtonHTMLAttributes } from "react";
import "./Button.css";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Variante del botón
   */
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "icon-primary-200"
    | "icon-primary-400"
    | "icon-primary-500"
    | "icon-primary-600"
    | "icon-bordered-default"
    | "icon-bordered-primary"
    | "icon-bordered-primary-light"
    | "icon-bordered-primary-dark"
    | "icon-transparent"
    | "icon-neutral-200"
    | "icon-neutral-300"
    | "icon-neutral-100"
    | "fab-primary-200"
    | "fab-primary-400"
    | "fab-primary-500"
    | "fab-primary-600";

  /**
   * Tamaño del botón
   */
  size?: "sm" | "md" | "lg" | "fab-sm" | "fab-lg";

  /**
   * Contenido del botón
   */
  children?: React.ReactNode;

  /**
   * Icono opcional para mostrar antes del texto
   */
  startIcon?: React.ReactNode;

  /**
   * Icono opcional para mostrar después del texto
   */
  endIcon?: React.ReactNode;

  /**
   * Si el botón ocupa todo el ancho disponible
   */
  fullWidth?: boolean;

  /**
   * Si el botón tiene borde
   */
  bordered?: boolean;

  /**
   * Si el botón es solo un icono
   */
  iconOnly?: boolean;

  /**
   * Si el botón es un FAB
   */
  isFab?: boolean;

  /**
   * Función que se ejecuta al hacer click en el botón
   */
  onClick?: () => void;

  /**
   * ID del botón para pruebas
   */
  testId?: string;

  /**
   * Si el botón está deshabilitado
   */
  disabled?: boolean;

  /**
   * Clase del botón
   */
  className?: string;

  /**
   * Tipo del botón
   */
  type?: "button" | "submit" | "reset";
}

/**
 * Componente de botón que utiliza los tokens de diseño
 */
export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  children,
  startIcon,
  endIcon,
  fullWidth = false,
  bordered = false,
  iconOnly = false,
  isFab = false,
  className = "",
  onClick,
  disabled = false,
  testId = null,
  type = "button",
  ...props
}) => {
  const buttonClasses = [
    "qida-button",
    `qida-button--${variant}`,
    `qida-button--${size}`,
    bordered ? "qida-button--bordered" : "",
    fullWidth ? "qida-button--full-width" : "",
    iconOnly ? "qida-button--icon-only" : "",
    isFab ? "qida-button--fab" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button className={buttonClasses} data-testid={testId} type="button" disabled={disabled} {...props} onClick={onClick}>
      {startIcon && <span className="qida-button__icon">{startIcon}</span>}
      {!iconOnly && <span className="qida-button__text">{children}</span>}
      {endIcon && <span className="qida-button__icon">{endIcon}</span>}
    </button>
  );
};

export default Button;
